import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import { previewPages } from '../../data/previewPages';
import { padNumber } from '../../utils/format';
type PageLightboxProps = {
  index: number;
  onChange: (index: number) => void;
  onClose: () => void;
};
export function PageLightbox({
  index,
  onChange,
  onClose
}: PageLightboxProps) {
  const [zoomed, setZoomed] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const page = previewPages[index];
  const total = previewPages.length;
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, []);
  useEffect(() => {
    setZoomed(false);
  }, [index]);
  const go = (step: number) => onChange((index + step + total) % total);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowRight') {
      go(1);
    } else if (event.key === 'ArrowLeft') {
      go(-1);
    } else if (event.key === 'Tab' && dialogRef.current) {
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>('button');
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };
  const controlClass = 'inline-flex h-12 min-w-[48px] items-center justify-center gap-2 border-2 border-gold px-3 font-bold text-gold transition-colors duration-150 hover:bg-gold hover:text-ink';
  return <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="lightbox-title" onKeyDown={handleKeyDown} className="fixed inset-0 z-[60] flex flex-col bg-black/95 text-white" data-id="element-367" data-mp="3m4lwnx53el9d">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-6" data-id="element-368" data-mp="3vuoohu0h9t64">
        <p id="lightbox-title" className="min-w-0 truncate text-base font-bold" data-id="element-369" data-mp="4p5cepuk7rw3">
          {page.resource} · Page {page.page}
        </p>
        <div className="flex shrink-0 items-center gap-2" data-id="element-370" data-mp="dn911v0ud4s4">
          <button type="button" onClick={() => setZoomed(value => !value)} aria-pressed={zoomed} className={controlClass} data-id="element-371" data-mp="dlo3dwj8d5q1">
            {zoomed ? <ZoomOutIcon aria-hidden="true" className="h-5 w-5" data-id="element-372" data-mp="2fh8a09qhra1n" /> : <ZoomInIcon aria-hidden="true" className="h-5 w-5" data-id="element-373" data-mp="2kdf8jhgk5j9k" />}
            <span className="hidden sm:inline" data-id="element-374" data-mp="2cupfx1lyu2wy">{zoomed ? 'Fit to screen' : 'Zoom in'}</span>
            <span className="sr-only sm:hidden" data-id="element-375" data-mp="2cwik8a93wb1s">{zoomed ? 'Fit to screen' : 'Zoom in'}</span>
          </button>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close preview" className={controlClass} data-id="element-376" data-mp="234moh5uhsx0q">
            <XIcon aria-hidden="true" className="h-5 w-5" data-id="element-377" data-mp="1y6ktpnnpf99f" />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto" data-id="element-378" data-mp="2vttne5xuzomi">
        {zoomed ? <div className="p-3 sm:p-6" data-id="element-379" data-mp="2quqeubio709s">
            <img src={page.src} alt={`Page ${page.page} of ${page.resource}: ${page.title}, enlarged`} className="mx-auto block h-auto w-[220%] max-w-none sm:w-full sm:max-w-[1152px]" data-id="element-380" data-mp="2qvs4bl0fnpb1" />
          </div> : <div className="flex h-full items-center justify-center p-3 sm:p-6" data-id="element-381" data-mp="1agqitbmy3urt">
            <img src={page.src} alt={`Page ${page.page} of ${page.resource}: ${page.title}`} className="block h-auto max-h-full w-auto max-w-full" data-id="element-382" data-mp="15h44eyvds8hf" />
          </div>}
      </div>

      <div className="flex items-center justify-center gap-4 border-t border-white/10 px-4 py-3" data-id="element-383" data-mp="2z33958k7axpw">
        <button type="button" onClick={() => go(-1)} aria-label="Previous page" className={controlClass} data-id="element-384" data-mp="2ke6toqfteif9">
          <ChevronLeftIcon aria-hidden="true" className="h-6 w-6" data-id="element-385" data-mp="2pd07yl2xedgh" />
        </button>
        <p className="min-w-[72px] text-center font-display text-2xl font-bold tabular-nums" aria-live="polite" data-id="element-386" data-mp="3d1jgn1pw0s93">
          {padNumber(index + 1)} / {padNumber(total)}
        </p>
        <button type="button" onClick={() => go(1)} aria-label="Next page" className={controlClass} data-id="element-387" data-mp="333vi00p5v0ln">
          <ChevronRightIcon aria-hidden="true" className="h-6 w-6" data-id="element-388" data-mp="382gcrkln8s2p" />
        </button>
      </div>
    </div>;
}